import { Form } from 'antd';

import {
  useFormSchema,
  useContractValidationScope,
  useValidationScope,
} from '@/components/form/FormSchemaContext';

import type { FormItemProps } from 'antd';

type FormFieldName = string | number | (string | number)[];

type SafeFormItemProps = Omit<FormItemProps, 'name' | 'required'> & {
  name?: FormFieldName;
  required?: boolean;
};

function toSchemaFieldName(
  name: FormFieldName,
): string | (string | number)[] | null {
  if (typeof name === 'string' || Array.isArray(name)) {
    return name;
  }
  return null;
}

/** Form.Item wrapper — required asterisk from FormSchema when field is registered. */
const FormItem = ({
  name,
  required: requiredProp,
  ...props
}: SafeFormItemProps) => {
  const formSchema = useFormSchema();
  const validationScope = useValidationScope();
  const contractValidationScope = useContractValidationScope();
  const form = Form.useFormInstance();
  const formValues = (Form.useWatch([], form) ?? {}) as Record<string, unknown>;

  const schemaFieldName = name != null ? toSchemaFieldName(name) : null;
  const schemaRequired: boolean | undefined =
    schemaFieldName != null && formSchema?.hasField(schemaFieldName)
      ? formSchema.isFieldRequired(
          schemaFieldName,
          formValues,
          validationScope,
          contractValidationScope,
        )
      : undefined;
  const resolvedRequired: boolean | undefined =
    (typeof requiredProp === 'boolean' ? requiredProp : undefined) ??
    schemaRequired;

  return (
    <Form.Item
      validateTrigger="onSubmit"
      name={name}
      {...(resolvedRequired !== undefined
        ? { required: resolvedRequired }
        : {})}
      {...props}
    />
  );
};

export default FormItem;
